function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function isHtmlResponse(value: string): boolean {
  return /^(<!doctype html|<html[\s>]|<head[\s>]|<body[\s>])/i.test(
    value.trim(),
  );
}

function extractErrorMessage(value: unknown): string | null {
  if (typeof value === "string") {
    const message = value.trim();
    if (message && !isHtmlResponse(message)) {
      return message;
    }

    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedMessage = extractErrorMessage(item);
      if (nestedMessage) {
        return nestedMessage;
      }
    }

    return null;
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const detail = extractErrorMessage(record.detail);
  if (detail) {
    return detail;
  }

  const errorText = extractErrorMessage(record.error);
  if (errorText) {
    return errorText;
  }

  for (const nestedValue of Object.values(record)) {
    const nestedMessage = extractErrorMessage(nestedValue);
    if (nestedMessage) {
      return nestedMessage;
    }
  }

  return null;
}

export function getApiErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  const record = asRecord(error);
  const response = asRecord(record?.response);
  const data = response?.data ?? record?.data;

  return extractErrorMessage(data) || fallbackMessage;
}

export function getApiErrorCode(error: unknown): string | null {
  const record = asRecord(error);
  const response = asRecord(record?.response);
  const data = asRecord(response?.data ?? record?.data);

  if (!data || typeof data.code !== "string" || !data.code.trim()) {
    return null;
  }

  return data.code;
}

export function getApiErrorStatus(error: unknown): number | null {
  const record = asRecord(error);
  const response = asRecord(record?.response);
  const status = response?.status ?? record?.status;

  if (typeof status === "number" && Number.isFinite(status)) {
    return status;
  }

  if (typeof status === "string" && status.trim()) {
    const parsedStatus = Number(status);
    return Number.isFinite(parsedStatus) ? parsedStatus : null;
  }

  return null;
}
